import React, { useState, useEffect } from 'react';

function SprintRaceBox({ race, drivers, teams, predictions, onDriverChange, onSubmit }) {
  const [loading, setLoading] = useState(false);
  const [timeUntilClose, setTimeUntilClose] = useState('Loading...');

  useEffect(() => {
    if (!race) {
      setTimeUntilClose('No race selected');
      return;
    }
    
    const sprintQualDateTime = race.sprintQualifyingDateTime;
    
    if (!sprintQualDateTime) {
      setTimeUntilClose('Countdown unavailable');
      return;
    }

    const updateTimer = () => {
      try {
        const now = new Date();
        const qualTime = new Date(sprintQualDateTime);
        
        if (isNaN(qualTime.getTime())) {
          setTimeUntilClose('Countdown unavailable');
          return;
        }
        
        const closeTime = new Date(qualTime.getTime() - 2 * 60 * 1000);
        const diff = closeTime - now;
        
        if (diff <= 0) {
          setTimeUntilClose('Predictions closed');
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          setTimeUntilClose(`${hours}h ${minutes}m ${seconds}s until predictions close`);
        }
      } catch (err) {
        setTimeUntilClose('Countdown unavailable');
      }
    };

    try {
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } catch (err) {
      setTimeUntilClose('Countdown unavailable');
    }
  }, [race]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit('sprint', predictions);
    } finally {
      setLoading(false);
    }
  };

  if (!race) return null;

  const sprintPreds = predictions.sprintPredictions || {};

  const getAvailableDriversForPosition = (currentIdx) => {
    const selectedDriverIds = sprintPreds.podium
      ?.map((driverId, idx) => {
        if (idx !== currentIdx && driverId) return driverId;
        return null;
      })
      .filter(Boolean) || [];

    return drivers.filter(driver => !selectedDriverIds.includes(driver.driverId));
  };

  const parsedSprintDate = race.sprintDate 
    ? new Date(race.sprintDate).toLocaleDateString('en-GB')
    : new Date(race.date).toLocaleDateString('en-GB');

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">👟 SPRINT RACE - {race.raceName}</h2>
      <p className="text-gray-600 mb-4">{parsedSprintDate}</p>

      {race.circuit && (
        <p className="text-gray-600 text-sm mb-6">
          {race.circuit.name} {race.circuit.location && `, ${race.circuit.location}`}
        </p>
      )}

      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-6 text-center">
        <p className="text-yellow-800 font-semibold">{timeUntilClose}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-50 p-4 rounded border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-4">Position Predictions (P1-P5)</h3>
          
          <div className="grid grid-cols-5 gap-2 mb-4">
            {['P1', 'P2', 'P3', 'P4', 'P5'].map((pos, idx) => (
              <div key={pos}>
                <label className="text-xs font-semibold text-gray-600 block mb-1">{pos}</label>
                <select
                  value={sprintPreds.podium?.[idx] || ''}
                  onChange={(e) => onDriverChange('sprint', 'podium', idx, e.target.value)}
                  className="w-full px-2 py-2 bg-gray-700 text-white text-sm rounded border border-gray-600 focus:border-yellow-500"
                  required={idx < 3}
                >
                  <option value="">Select</option>
                  {getAvailableDriversForPosition(idx).map(driver => (
                    <option key={driver._id} value={driver.driverId}>
                      {driver.code}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-4">Other Predictions (1 point each)</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Pole Position</label>
              <select
                value={sprintPreds.polePosition || ''}
                onChange={(e) => onDriverChange('sprint', 'polePosition', null, e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500"
              >
                <option value="">Select driver</option>
                {drivers.map(driver => (
                  <option key={driver._id} value={driver.driverId}>
                    {driver.code}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Fastest Lap</label>
              <select
                value={sprintPreds.fastestLap || ''}
                onChange={(e) => onDriverChange('sprint', 'fastestLap', null, e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500"
              >
                <option value="">Select driver</option>
                {drivers.map(driver => (
                  <option key={driver._id} value={driver.driverId}>
                    {driver.code}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">First Retirement</label>
              <select
                value={sprintPreds.firstRetirement || ''}
                onChange={(e) => onDriverChange('sprint', 'firstRetirement', null, e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500"
              >
                <option value="">Select driver</option>
                {drivers.map(driver => (
                  <option key={driver._id} value={driver.driverId}>
                    {driver.code}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Most Places Gained</label>
              <select
                value={sprintPreds.mostPlacesGained || ''}
                onChange={(e) => onDriverChange('sprint', 'mostPlacesGained', null, e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500"
              >
                <option value="">Select driver</option>
                {drivers.map(driver => (
                  <option key={driver._id} value={driver.driverId}>
                    {driver.code}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-4">Head-to-Head (1 point each)</h3>
          
          {!race?.headToHead?.sprint?.drivers?.driver1 || !race?.headToHead?.sprint?.drivers?.driver2 ? (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              No driver matchup has been set for this race yet.
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-4">Driver Head-to-Head</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Driver 1</label>
                    <div className="p-2 border rounded bg-gray-700 text-white">
                      {race.headToHead.sprint.drivers.driver1.code}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Driver 2</label>
                    <div className="p-2 border rounded bg-gray-700 text-white">
                      {race.headToHead.sprint.drivers.driver2.code}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Winner</label>
                    <select
                      value={sprintPreds.driverHeadToHead?.winner || ''}
                      onChange={(e) => onDriverChange('sprint', 'driverHeadToHead', 'winner', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500"
                    >
                      <option value="">Select winner</option>
                      <option value={race.headToHead.sprint.drivers.driver1.driverId}>
                        {race.headToHead.sprint.drivers.driver1.code}
                      </option>
                      <option value={race.headToHead.sprint.drivers.driver2.driverId}>
                        {race.headToHead.sprint.drivers.driver2.code}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!race?.headToHead?.sprint?.teams?.team1 || !race?.headToHead?.sprint?.teams?.team2 ? (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mt-4">
              No team matchup has been set for this race yet.
            </div>
          ) : (
            <div className="space-y-4 mt-6">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-4">Team Head-to-Head</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Team 1</label>
                    <div className="p-2 border rounded bg-gray-700 text-white">
                      {race.headToHead.sprint.teams.team1.name}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Team 2</label>
                    <div className="p-2 border rounded bg-gray-700 text-white">
                      {race.headToHead.sprint.teams.team2.name}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">Winner</label>
                    <select
                      value={sprintPreds.teamHeadToHead?.winner || ''}
                      onChange={(e) => onDriverChange('sprint', 'teamHeadToHead', 'winner', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-yellow-500"
                    >
                      <option value="">Select winner</option>
                      <option value={race.headToHead.sprint.teams.team1.name}>
                        {race.headToHead.sprint.teams.team1.name}
                      </option>
                      <option value={race.headToHead.sprint.teams.team2.name}>
                        {race.headToHead.sprint.teams.team2.name}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-500 text-white font-bold py-3 px-4 rounded transition-colors"
        >
          {loading ? 'Submitting...' : 'Submit Sprint Predictions'}
        </button>
      </form>
    </div>
  );
}

export default SprintRaceBox;